import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Button, AppBar, Toolbar, IconButton, Drawer, List, 
  ListItem, ListItemText, ListItemIcon, Tooltip, Box, Typography, Divider,
  BottomNavigation, BottomNavigationAction, Paper, useMediaQuery, useTheme,
  Avatar
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import gridoIcon from '../images/grido.jpg';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useModoOscuro } from '../contexts/ModoOscuro';
import { registrarAccion, isAuthenticated } from '../services/api.js';
import './estilos.css';
import {
  API_BASE_URL,
  VERIFICARVENCIMIENTO,
} from "../services/apiConfig";
import { MenuItemsConfig, MobileMainMenuConfig } from '../config/menuItems.jsx';
import axios from 'axios';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Ver comentario junto al useEffect que lo usa más abajo.
let instanciasMenuInicial = 0;

export const MenuInicial = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Detecta móvil/tablet
  
  const userData = JSON.parse(sessionStorage.getItem("userData"));
  const perfil = userData?.perfil || 3;
  const perfilNombre = userData?.perfil_nombre || (perfil === 1 ? 'Administrador' : perfil === 2 ? 'Gerente' : 'Empleado');
  const rawSucursalid = userData?.sucursalid;
  const sucursalNombre = rawSucursalid && rawSucursalid !== 999 ? userData?.sucursalnombre : "Ninguna";
  const auditaroperaciones = userData?.auditaroperaciones;
  const usuarioid = userData?.usuarioid;
  const nombreUsuario = userData?.username || userData?.nombre || "Usuario";
  
  const [pinned, setPinned] = useState(() => localStorage.getItem('menu_pinned') === '1');
  const { oscuro, toggleModoOscuro } = useModoOscuro();
  const [open, setOpen] = useState(() => localStorage.getItem('menu_pinned') === '1');
  const [bottomNavValue, setBottomNavValue] = useState(0);
  const [horaActual, setHoraActual] = useState(new Date());
  const [colorSistema, setColorSistema] = useState(() => localStorage.getItem('color_sistema') || '#071c44');
  const [iconColor, setIconColor] = useState(() => localStorage.getItem('color_icono') || '#a9b9a6');
  const [licenciaInfo, setLicenciaInfo] = useState(null);
  const [licenciaLoaded, setLicenciaLoaded] = useState(false);

  useEffect(() => {
    axios.get(API_BASE_URL + VERIFICARVENCIMIENTO, { headers: authHeader() })
      .then(res => {
        if (res.data?.data) {
          setLicenciaInfo(res.data.data);
        }
      })
      .catch(() => {})
      .finally(() => setLicenciaLoaded(true));
  }, []);

  useEffect(() => {
    setColorSistema(localStorage.getItem('color_sistema') || '#071c44');
    setIconColor(localStorage.getItem('color_icono') || '#a9b9a6');
  }, [location.pathname]);

  const currentMenuItems = MenuItemsConfig[perfil] || MenuItemsConfig[3];
  const mobileMainMenu = MobileMainMenuConfig[perfil] || MobileMainMenuConfig[3];

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const timer = setInterval(() => setHoraActual(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Actualizar valor de bottom navigation según la ruta actual
  useEffect(() => {
    const currentIndex = mobileMainMenu.findIndex(item => item.route.split('?')[0] === location.pathname);
    if (currentIndex !== -1) {
      setBottomNavValue(currentIndex);
    }
  }, [location, mobileMainMenu]);

  const handleNavigation = async (destination) => {
    if (Number(auditaroperaciones) === 1) {
      await registrarAccion(usuarioid, ` navegó a ${destination}`);
    }
    navigate(destination);
    if (!pinned) setOpen(false);
};

  const togglePinned = () => {
    setPinned((prev) => {
      const next = !prev;
      localStorage.setItem('menu_pinned', next ? '1' : '0');
      setOpen(next); // pinear abre el menú; despinear lo cierra
      return next;
    });
  };

  // Menú pineado (desktop): reserva el ancho del Drawer para que el
  // contenido de la página no quede tapado, sin tocar cada pantalla.
  useEffect(() => {
    if (!isMobile && pinned && open) {
      document.body.classList.add('menu-pinned');
    } else {
      document.body.classList.remove('menu-pinned');
    }
  }, [pinned, open, isMobile]);

  // Cada página monta su propio <MenuInicial />, así que al navegar hay una
  // instancia vieja desmontándose y una nueva montándose casi al mismo
  // tiempo (la nueva tarda un toque en cargar sus datos). Sacar la clase al
  // toque en el desmontaje hacía que el padding-left del menú pineado
  // parpadeara a 0 en cada navegación ("se rompe el menú"). Retrasando la
  // remoción y cancelándola si ya montó otra instancia, se evita el
  // parpadeo sin convertir el menú en un layout persistente.
  useEffect(() => {
    instanciasMenuInicial += 1;
    return () => {
      instanciasMenuInicial -= 1;
      setTimeout(() => {
        if (instanciasMenuInicial === 0) document.body.classList.remove('menu-pinned');
      }, 150);
    };
  }, []);

  // Renderizado para MÓVIL/TABLET
  if (licenciaInfo && licenciaInfo.expirado && perfil !== 1) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#fdecea', p: 3, textAlign: 'center' }}>
        <Typography variant="h4" color="error" gutterBottom fontWeight={700}>
          Licencia Vencida
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 400 }}>
          La licencia del sistema ha expirado. Contacte al administrador para renovarla.
        </Typography>
        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Vencimiento: {licenciaInfo.fecha_vencimiento ? new Date(licenciaInfo.fecha_vencimiento + 'T12:00:00').toLocaleDateString('es-AR') : '-'}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <>
        {licenciaInfo && !licenciaInfo.expirado && licenciaInfo.dias_restantes !== null && licenciaInfo.dias_restantes <= 7 && (
          <Box sx={{ bgcolor: '#fff3e0', p: 1, textAlign: 'center', borderBottom: '1px solid #ffcc80' }}>
            <Typography variant="caption" fontWeight={600} color="warning.dark">
              Licencia vence en {licenciaInfo.dias_restantes} día{licenciaInfo.dias_restantes === 1 ? '' : 's'}
            </Typography>
          </Box>
        )}
        {/* Header minimalista para móvil */}
        <AppBar 
          position="fixed" 
          sx={{ 
            backgroundColor: colorSistema,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Toolbar sx={{ minHeight: '56px !important' }}>
            <IconButton edge="start" color="inherit" onClick={() => setOpen(true)}>
              <MenuIcon sx={{ color: iconColor }} />
            </IconButton>
            
            <Box sx={{ flexGrow: 1, ml: 2 }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: '600', fontSize: '1.1rem' }}>
                Gestion de Locales
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                {perfilNombre} · {sucursalNombre}
              </Typography>
            </Box>

            <IconButton onClick={toggleModoOscuro} sx={{ mr: 0.5 }}>
              <Tooltip title={oscuro ? 'Modo claro' : 'Modo oscuro'}>
                {oscuro ? <LightModeIcon sx={{ color: iconColor }} /> : <DarkModeIcon sx={{ color: iconColor }} />}
              </Tooltip>
            </IconButton>

            <Avatar
              onClick={() => handleNavigation('/user')}
              sx={{
                width: 32,
                height: 32,
                bgcolor: iconColor,
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              {nombreUsuario.charAt(0).toUpperCase()}
            </Avatar>
          </Toolbar>
        </AppBar>

        {/* Spacer para que el contenido no quede debajo del AppBar */}
        <Toolbar sx={{ minHeight: '56px !important' }} />

        {/* Bottom Navigation */}
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1000,
            borderTop: '1px solid rgba(0,0,0,0.12)'
          }} 
          elevation={3}
        >
          <BottomNavigation
            value={bottomNavValue}
            onChange={(event, newValue) => {
              setBottomNavValue(newValue);
              const selectedItem = mobileMainMenu[newValue];
              if (selectedItem.route === "more") {
                setOpen(true);
              } else {
                handleNavigation(selectedItem.route);
              }
            }}
            showLabels
            sx={{
              height: 65,
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                padding: '6px 0',
              },
              '& .Mui-selected': {
                color: colorSistema,
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                marginTop: '4px'
              }
            }}
          >
            {mobileMainMenu.map((item, index) => (
              <BottomNavigationAction
                key={index}
                label={item.title}
                icon={React.cloneElement(item.icon, {
                  sx: {
                    fontSize: '1.5rem',
                    color: bottomNavValue === index ? colorSistema : 'rgba(0,0,0,0.6)'
                  }
                })}
              />
            ))}
          </BottomNavigation>
        </Paper>

        {/* Drawer lateral con todas las opciones */}
        <Drawer 
          anchor="left" 
          open={open} 
          onClose={() => setOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              overflow: 'auto',
            }
          }}
        >
          <Box>
            {/* Header del drawer */}
            <Box 
              sx={{ 
                p: 3, 
                background: `linear-gradient(135deg, ${colorSistema} 0%, ${colorSistema}dd 100%)`,
                color: 'white',
              }}
            >
              <Box
                component="img"
                src={gridoIcon}
                alt="Grido"
                sx={{ height: 44, display: 'block', mx: 'auto', mb: 1.5, borderRadius: 1 }}
              />
              <Box
                onClick={() => handleNavigation('/user')}
                sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }}
              >
                <Avatar
                  sx={{
                    width: 50,
                    height: 50,
                    bgcolor: iconColor,
                    mr: 2,
                    fontSize: '1.3rem'
                  }}
                >
                  {nombreUsuario.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: '600', mb: 1, lineHeight: 1.2 }}>
                    Hola, {nombreUsuario}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.3 }}>
                    {perfilNombre}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Sucursal: {sucursalNombre}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Lista de opciones */}
            <List sx={{ pt: 2 }}>
              {currentMenuItems.map((item, index) => (
                <ListItem 
                  button 
                  key={index} 
                  onClick={() => handleNavigation(item.route)}
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: `${colorSistema}10`,
                    },
                    borderLeft: location.pathname === item.route ? `4px solid ${colorSistema}` : '4px solid transparent',
                  }}
                >
                  <ListItemIcon>
                    {React.cloneElement(item.icon, {
                      sx: {
                        color: location.pathname === item.route ? colorSistema : 'rgba(0,0,0,0.6)',
                        fontSize: '1.4rem'
                      }
                    })}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.title}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      fontWeight: location.pathname === item.route ? '600' : '400',
                      color: location.pathname === item.route ? colorSistema : 'inherit'
                    }}
                  />
                </ListItem>
              ))}
              
              <Divider sx={{ my: 1 }} />
              
              {/* Cerrar Sesión */}
              <ListItem 
                button 
                onClick={() => handleNavigation("/logout")}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                  }
                }}
              >
                <ListItemIcon>
                  <CloseIcon sx={{ color: '#d32f2f', fontSize: '1.4rem' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Cerrar Sesión"
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    color: '#d32f2f'
                  }}
                />
              </ListItem>
              <Box sx={{ textAlign: 'center', py: 1.5, px: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {horaActual.toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: '600', color: 'text.primary', mt: 0.3 }}>
                  {horaActual.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                </Typography>
              </Box>
            </List>
          </Box>
        </Drawer>

        {/* Spacer para que el contenido no quede debajo del bottom nav */}
        <Box sx={{ height: 65 }} />
      </>
    );
  }

  // Renderizado para DESKTOP (tu menú actual)
  return (
    <Box className="miDiv">
      {licenciaInfo && !licenciaInfo.expirado && licenciaInfo.dias_restantes !== null && licenciaInfo.dias_restantes <= 7 && (
        <Box sx={{ bgcolor: '#fff3e0', p: 1, textAlign: 'center', borderBottom: '1px solid #ffcc80' }}>
          <Typography variant="caption" fontWeight={600} color="warning.dark">
            Licencia vence en {licenciaInfo.dias_restantes} día{licenciaInfo.dias_restantes === 1 ? '' : 's'}
          </Typography>
        </Box>
      )}
      {/* Pineado: el Drawer persistente ya muestra usuario/perfil/sucursal,
          todo el menú y "Cerrar sesión" — esta barra queda redundante y solo
          resta espacio vertical, así que se oculta mientras está pineado. */}
      {!pinned && (
        <AppBar position="static" style={{ backgroundColor: colorSistema }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => setOpen(true)}>
              <MenuIcon />
            </IconButton>

            <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', ml: 2, overflowX: 'auto', '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: 2 } }}>
              {currentMenuItems.map((item, index) => (
                <Button
                  key={index}
                  variant="text"
                  onClick={() => handleNavigation(item.route)}
                  sx={{
                    color: '#ffffff',
                    minWidth: '44px',
                    flexShrink: 0,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  <Tooltip title={item.title}>
                    {React.cloneElement(item.icon, { sx: { color: iconColor } })}
                  </Tooltip>
                </Button>
              ))}
            </Box>

            <Box
              onClick={() => handleNavigation('/user')}
              sx={{ mr: 2, textAlign: 'right', cursor: 'pointer' }}
            >
              <Typography variant="body1" sx={{ color: 'white', fontWeight: '500', lineHeight: 1.2 }}>
                Hola, {nombreUsuario}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem' }}>
                {perfilNombre} · {sucursalNombre}
              </Typography>
            </Box>

            <IconButton onClick={toggleModoOscuro}>
              <Tooltip title={oscuro ? 'Modo claro' : 'Modo oscuro'}>
                {oscuro ? <LightModeIcon sx={{ color: iconColor }} /> : <DarkModeIcon sx={{ color: iconColor }} />}
              </Tooltip>
            </IconButton>

            <Button variant="text" onClick={() => handleNavigation("/logout")} style={{ color: '#ffffff' }}>
              <Tooltip title="Cerrar sesión"><CloseIcon sx={{ color: iconColor }} /></Tooltip>
            </Button>
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        anchor="left"
        variant={pinned ? 'persistent' : 'temporary'}
        open={open}
        onClose={() => setOpen(false)}
      >
        <Box sx={{ width: 250 }}>
          <Box sx={{ p: 2, backgroundColor: colorSistema, color: 'white', textAlign: 'center', position: 'relative' }}>
            <Tooltip title={oscuro ? 'Modo claro' : 'Modo oscuro'}>
              <IconButton
                size="small"
                onClick={toggleModoOscuro}
                sx={{ position: 'absolute', top: 4, left: 4, color: iconColor }}
              >
                {oscuro ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title={pinned ? 'Desfijar menú' : 'Fijar menú'}>
              <IconButton
                size="small"
                onClick={togglePinned}
                sx={{ position: 'absolute', top: 4, right: 4, color: iconColor }}
              >
                {pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Box
              component="img"
              src={gridoIcon}
              alt="Grido"
              sx={{ height: 44, display: 'block', mx: 'auto', mb: 1.5, borderRadius: 1 }}
            />
            <Box onClick={() => handleNavigation('/user')} sx={{ cursor: 'pointer' }}>
              <AccountCircleIcon sx={{ fontSize: 40, mb: 1, color: iconColor }} />
              <Typography variant="h6">Hola, {nombreUsuario}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Perfil: {perfilNombre}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Sucursal: {sucursalNombre}
              </Typography>
            </Box>
          </Box>
          <Divider />
          
          <List>
            {currentMenuItems.map((item, index) => (
              <ListItem button key={index} onClick={() => handleNavigation(item.route)}>
                <ListItemIcon>
                  {React.cloneElement(item.icon, { sx: { color: colorSistema } })}
                </ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItem>
            ))}
            <Divider />
            <ListItem button onClick={() => handleNavigation("/logout")}>
              <ListItemIcon><CloseIcon sx={{ color: 'red' }} /></ListItemIcon>
              <ListItemText primary="Cerrar Sesión" />
            </ListItem>
            <Box sx={{ textAlign: 'center', py: 1.5, px: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {horaActual.toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: '600', color: 'text.primary', mt: 0.3 }}>
                {horaActual.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
              </Typography>
            </Box>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default MenuInicial;
